import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Pause, Play, ArrowClockwise, DownloadSimple } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function JobDetail() {
  const { jid } = useParams();
  const [job, setJob] = useState(null);
  const [recon, setRecon] = useState(null);
  const [errors, setErrors] = useState([]);
  const [skipped, setSkipped] = useState([]);

  const load = async () => {
    const [j, r] = await Promise.all([api.get(`/jobs/${jid}`), api.get(`/jobs/${jid}/reconciliation`)]);
    setJob(j.data); setRecon(r.data);
    if (j.data.failed > 0) api.get(`/jobs/${jid}/errors`).then((e) => setErrors(e.data));
    if (j.data.skipped > 0) api.get(`/jobs/${jid}/records`).then((e) => setSkipped(e.data.filter((x) => x.status === "skipped")));
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 2500);
    return () => clearInterval(iv);
  }, [jid]);

  const act = async (path, label) => {
    try { await api.post(`/jobs/${jid}/${path}`); toast.success(label); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const download = (path, name) => {
    const link = document.createElement("a");
    link.href = `${API}/jobs/${jid}/${path}`;
    link.download = name;
    // include cookies
    fetch(link.href, { credentials: "include" }).then((r) => r.blob()).then((b) => {
      const url = URL.createObjectURL(b); const a = document.createElement("a");
      a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    });
  };

  if (!job) return <Layout><div className="p-8 text-sm text-stone-500">Loading…</div></Layout>;

  return (
    <Layout>
      <PageHeader title={job.project_name} subtitle={`Migration job · ${job.mode}`}
        actions={
          <div className="flex gap-2">
            {job.status === "running" && <Button data-testid="pause-job" size="sm" variant="outline" onClick={() => act("pause", "Pausing…")} className="rounded-xl gap-1.5"><Pause size={14} /> Pause</Button>}
            {["paused", "failed"].includes(job.status) && <Button data-testid="resume-job" size="sm" onClick={() => act("resume", "Resuming…")} className="rounded-xl gap-1.5 bg-[#C2410C] hover:bg-[#C2410C] transition-colors duration-200"><Play size={14} /> Resume</Button>}
            {job.failed > 0 && <Button data-testid="retry-job" size="sm" variant="outline" onClick={() => act("retry", "Retrying failures…")} className="rounded-xl gap-1.5"><ArrowClockwise size={14} /> Retry Failures</Button>}
          </div>
        }
      />
      <div className="p-8 space-y-6">
        {/* progress */}
        <div className="bg-white border border-stone-200 rounded-2xl shadow-card p-6">
          <div className="flex items-center justify-between mb-3">
            <StatusBadge status={job.status} />
            <span className="font-mono text-sm text-stone-500">{job.progress_pct}%</span>
          </div>
          <Progress value={job.progress_pct} className="h-2 rounded-xl" />
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-6">
            <Metric label="Total" value={job.total} />
            <Metric label="Processed" value={job.processed} />
            <Metric label="Successful" value={job.successful} cls="text-emerald-600" />
            <Metric label="Failed" value={job.failed} cls="text-rose-600" />
            <Metric label="Skipped" value={job.skipped} cls="text-amber-600" />
            <Metric label="Created" value={job.created} cls="text-[#C2410C]" />
          </div>
          {job.error && <div className="mt-4 text-xs text-rose-600 bg-rose-50 border border-rose-200 p-3 rounded-xl font-mono">{job.error}</div>}
        </div>

        <Tabs defaultValue="reconciliation">
          <TabsList className="rounded-xl">
            <TabsTrigger value="reconciliation" data-testid="tab-recon" className="rounded-xl">Reconciliation</TabsTrigger>
            <TabsTrigger value="errors" data-testid="tab-errors" className="rounded-xl">Error Log ({job.failed})</TabsTrigger>
            <TabsTrigger value="skipped" data-testid="tab-skipped" className="rounded-xl">Skipped ({job.skipped})</TabsTrigger>
          </TabsList>

          <TabsContent value="reconciliation" className="mt-4">
            <div className="bg-white border border-stone-200 rounded-2xl shadow-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-head font-semibold text-stone-950">Reconciliation Summary</h3>
                <Button size="sm" variant="outline" onClick={() => download("error-report", `errors_${jid}.csv`)} className="rounded-xl gap-1.5 text-xs"><DownloadSimple size={14} /> Error Report</Button>
              </div>
              {recon && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 border border-stone-200 rounded-2xl shadow-card overflow-hidden">
                  <ReconCell label="Source Count" value={recon.source_count} />
                  <ReconCell label="Processed" value={recon.processed} />
                  <ReconCell label="Target Created" value={recon.target_created} />
                  <ReconCell label="Target Updated" value={recon.target_updated} />
                  <ReconCell label="Successful" value={recon.successful} />
                  <ReconCell label="Skipped" value={recon.skipped} />
                  <ReconCell label="Failed" value={recon.failed} />
                  <ReconCell label="Outstanding Errors" value={recon.outstanding_errors} />
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="errors" className="mt-4">
            <div className="bg-white border border-stone-200 rounded-2xl shadow-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                    <th className="px-6 py-3 font-semibold">Source ID</th>
                    <th className="px-6 py-3 font-semibold">Object</th>
                    <th className="px-6 py-3 font-semibold">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((e) => (
                    <tr key={e.id} className="border-b border-stone-100">
                      <td className="px-6 py-3 font-mono text-xs">{e.source_id}</td>
                      <td className="px-6 py-3 text-xs">{e.object_mapping}</td>
                      <td className="px-6 py-3 text-xs text-rose-600">{e.error}</td>
                    </tr>
                  ))}
                  {errors.length === 0 && <tr><td colSpan={3} className="px-6 py-10 text-center text-stone-400 text-sm">No errors.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="skipped" className="mt-4">
            <div className="bg-white border border-stone-200 rounded-2xl shadow-card overflow-x-auto">
              <div className="px-6 py-3 border-b border-stone-200 text-xs text-stone-500">
                Records skipped by duplicate-prevention — their source ID was already migrated (idempotent re-run).
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                    <th className="px-6 py-3 font-semibold">Source ID</th>
                    <th className="px-6 py-3 font-semibold">Existing Target ID</th>
                    <th className="px-6 py-3 font-semibold">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {skipped.map((e) => (
                    <tr key={e.id} className="border-b border-stone-100">
                      <td className="px-6 py-3 font-mono text-xs">{e.source_id}</td>
                      <td className="px-6 py-3 font-mono text-xs text-[#C2410C]">{e.target_id || "—"}</td>
                      <td className="px-6 py-3 text-xs text-amber-700">{e.error}</td>
                    </tr>
                  ))}
                  {skipped.length === 0 && <tr><td colSpan={3} className="px-6 py-10 text-center text-stone-400 text-sm">No skipped records.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

const Metric = ({ label, value, cls }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-stone-400">{label}</div>
    <div className={`stat-num text-2xl font-bold mt-1 ${cls || "text-stone-900"}`}>{value}</div>
  </div>
);

const ReconCell = ({ label, value }) => (
  <div className="bg-white p-4">
    <div className="text-xs uppercase tracking-wider text-stone-400">{label}</div>
    <div className="stat-num text-xl font-bold mt-1 text-stone-900">{value}</div>
  </div>
);
